import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, AlertCircle, HelpCircle } from 'lucide-react';

const SECTION_INITIAL = { opacity: 0, y: 30 };
const SECTION_IN_VIEW = { opacity: 1, y: 0 };
const SECTION_VIEWPORT = { once: true, margin: '-80px' };
const SECTION_TRANSITION = { duration: 0.6, ease: [0.22, 1, 0.36, 1] };
const ITEM_INITIAL = { opacity: 0, y: 10 };
const ITEM_IN_VIEW = { opacity: 1, y: 0 };
const ONCE = { once: true };

const ConfidenceBadge = ({ confidence }) => {
  const map = {
    confirmed: { color: '#10B981', border: 'rgba(16,185,129,0.3)', label: 'Verified', Icon: CheckCircle },
    inferred: { color: '#F59E0B', border: 'rgba(245,158,11,0.3)', label: 'Inferred', Icon: AlertCircle },
    missing: { color: '#A39A94', border: 'rgba(100,116,139,0.3)', label: 'Missing', Icon: HelpCircle },
  };
  const cfg = map[confidence] || map.missing;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '4px',
      fontFamily: 'var(--font-mono)', fontSize: '0.6rem',
      color: cfg.color, letterSpacing: '0.05em',
    }}>
      <cfg.Icon size={10} />
    </span>
  );
};

const ACRONYMS = { it: 'IT', ielts: 'IELTS', pte: 'PTE', toefl: 'TOEFL', uk: 'UK', uae: 'UAE' };
const prettify = (s) => s.split(/\s+/).map(w => ACRONYMS[w.toLowerCase()] || (w.charAt(0).toUpperCase() + w.slice(1))).join(' ');

const ProfileSnapshot = ({ summary }) => {
  if (!summary || Object.keys(summary).length === 0) return null;

  const formatKey = (key) => key.replace(/_/g, ' ');
  const formatValue = (val) => {
    if (Array.isArray(val)) return val.join(', ');
    if (typeof val === 'boolean') return val ? 'Yes' : 'No';
    if (typeof val === 'object' && val !== null) {
      const inner = val.value;
      if (inner === null || inner === undefined || inner === '') return 'Missing';
      if (Array.isArray(inner)) return inner.join(', ');
      if (typeof inner === 'boolean') return inner ? 'Yes' : 'No';
      return typeof inner === 'string' ? prettify(inner) : String(inner);
    }
    return typeof val === 'string' && val === val.toLowerCase() ? prettify(val) : String(val);
  };
  const getConfidence = (val) => {
    if (typeof val === 'object' && val !== null && !Array.isArray(val)) return val.confidence || 'confirmed';
    return 'confirmed';
  };

  const entries = Object.entries(summary).filter(([key, val]) => {
    if (key === 'clb_modules') return false;
    const display = formatValue(val);
    return display && display !== 'null' && display !== 'undefined';
  });

  if (entries.length === 0) return null;

  return (
    <motion.section
      data-testid="profile-snapshot-section"
      initial={SECTION_INITIAL}
      whileInView={SECTION_IN_VIEW}
      viewport={SECTION_VIEWPORT}
      transition={SECTION_TRANSITION}
      style={{
        padding: '0 1.5rem',
        maxWidth: '900px',
        margin: '0 auto 4rem',
      }}
    >
      <div style={{
        fontFamily: 'var(--font-body)',
        fontSize: '0.7rem',
        fontWeight: 700,
        letterSpacing: '0.25em',
        textTransform: 'uppercase',
        color: '#F67425',
        marginBottom: '1rem',
      }}>
        Your Profile
      </div>

      <div style={{
        background: '#fff',
        border: '1px solid rgba(48,43,44,0.1)',
        borderRadius: '2px',
        boxShadow: '0 12px 32px -12px rgba(48,43,44,0.1)',
        padding: '1.5rem 2rem',
      }}>
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '0',
        }}>
          {entries.map(([key, val], i) => {
            const display = formatValue(val);
            const confidence = getConfidence(val);
            return (
              <motion.div
                key={key}
                initial={ITEM_INITIAL}
                whileInView={ITEM_IN_VIEW}
                viewport={ONCE}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                style={{
                  flex: '1 1 auto',
                  minWidth: '140px',
                  padding: '0.75rem 1.25rem',
                  borderRight: i < entries.length - 1 ? '1px solid rgba(48,43,44,0.08)' : 'none',
                }}
              >
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '6px',
                  marginBottom: '4px',
                }}>
                  <span style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: '0.6rem',
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    color: 'var(--text-muted)',
                  }}>
                    {formatKey(key)}
                  </span>
                  <ConfidenceBadge confidence={confidence} />
                </div>
                <div style={{
                  fontFamily: 'var(--font-heading)',
                  fontSize: '1rem',
                  fontWeight: 700,
                  color: '#302B2C',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}>
                  {display}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </motion.section>
  );
};

export default ProfileSnapshot;
